<template>
  <div>
    <!-- Header card -->
    <b-row class="mb-1">
      <b-col>
        <b-card>
          <b-row align-v="center">
            <b-col>
              <b-card-title class="mb-0">
                Marketplace Dashboard
              </b-card-title>
            </b-col>
            <b-col
              lg="auto"
              class="mt-1 mt-lg-0"
            >
              <b-row
                align-v="center"
                no-gutters
              >
                <b-col
                  cols="auto"
                  class="mr-50"
                >
                  Product weighting
                </b-col>
                <b-col cols="auto">
                  <b-button
                    variant="outline-primary"
                    disabled
                    style="min-width: 220px"
                    class="d-flex justify-content-between align-items-center"
                  >
                    Equally weighted
                    <feather-icon
                      icon="ChevronDownIcon"
                      class="ml-50"
                    />
                  </b-button>
                </b-col>
              </b-row>
            </b-col>
          </b-row>
        </b-card>
      </b-col>
    </b-row>

    <!-- Row 1: Performance Summary + Evolution -->
    <b-row class="match-height">
      <b-col
        cols="12"
        lg="6"
      >
        <b-card
          no-body
          class="performance-card"
        >
          <div class="card-body pb-0">
            <b-card-title class="mb-0">
              Performance Summary
            </b-card-title>
          </div>
          <b-table
            responsive
            :fields="performanceFields"
            :items="performanceItems"
          />
        </b-card>
      </b-col>
      <b-col
        cols="12"
        lg="6"
      >
        <portfolio-evolution-chart
          portfolio-type="u"
          :evolution-data="evolutionData"
          index-underlying-vol-ratio="51.9%"
        />
      </b-col>
    </b-row>

    <!-- Row 2: Matured Products + Future Maturities -->
    <b-row class="match-height">
      <b-col
        cols="12"
        lg="6"
      >
        <matured-products-chart :returns-data="portfolioData" />
      </b-col>
      <b-col
        cols="12"
        lg="6"
      >
        <future-maturities-chart :future-maturities-data="portfolioData" />
      </b-col>
    </b-row>

    <!-- Row 3: Scatter charts -->
    <b-row class="match-height">
      <b-col
        cols="12"
        lg="6"
      >
        <payoff-scatter-chart :payoff-scatter="payoffScatterData" />
      </b-col>
      <b-col
        cols="12"
        lg="6"
      >
        <levels-scatter-chart :levels-scatter-data="levelsScatterData" />
      </b-col>
    </b-row>

    <!-- Row 4: Breakdown Options -->
    <b-row>
      <b-col>
        <b-card>
          <div class="d-flex flex-column">
            <b-card-title class="mb-0">
              Product Breakdowns
            </b-card-title>
            <div class="font-small-3 mb-1 text-muted">
              Charts are generated from the subset choices below.
            </div>
            <div class="d-flex flex-wrap mt-2 align-items-center">
              <div class="d-flex align-items-center">
                <span class="mr-1">Product subset</span>
                <b-button
                  variant="outline-primary"
                  disabled
                  style="min-width: 150px"
                  class="d-flex justify-content-between align-items-center"
                >
                  All
                  <feather-icon
                    icon="ChevronDownIcon"
                    class="ml-50"
                  />
                </b-button>
              </div>
              <span class="pr-1 mt-1 pl-0 pl-xl-3 mt-xl-0">
                Live products plus those matured in the selected range
              </span>
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <!-- Row 5: Breakdown Charts -->
    <b-row class="match-height mt-0">
      <b-col md="4">
        <b-card>
          <b-card-title class="mb-0">
            Underlyings
          </b-card-title>
          <div class="font-small-3 mb-1 text-muted">
            Marketplace breakdown by underlying showing proportion and return
          </div>
          <b-row class="match-height">
            <b-col cols="7">
              <app-echart-bar
                :option-data="breakdown.underlyings.holdingOptions"
                :height="chartHeights.underlyings + 'px'"
              />
            </b-col>
            <b-col cols="5">
              <app-echart-bar
                :option-data="breakdown.underlyings.returnOptions"
                :height="chartHeights.underlyings + 'px'"
              />
            </b-col>
          </b-row>
        </b-card>
      </b-col>

      <b-col md="4">
        <b-card>
          <b-card-title class="mb-0">
            Counterparties
          </b-card-title>
          <div class="font-small-3 mb-1 text-muted">
            Marketplace breakdown by counterparty showing proportion and return
          </div>
          <b-row class="match-height">
            <b-col cols="7">
              <app-echart-bar
                :option-data="breakdown.issuers.holdingOptions"
                :height="chartHeights.issuers + 'px'"
              />
            </b-col>
            <b-col cols="5">
              <app-echart-bar
                :option-data="breakdown.issuers.returnOptions"
                :height="chartHeights.issuers + 'px'"
              />
            </b-col>
          </b-row>
        </b-card>
      </b-col>

      <b-col md="4">
        <b-card>
          <b-card-title class="mb-0">
            Product Types
          </b-card-title>
          <div class="font-small-3 mb-1 text-muted">
            Marketplace breakdown by type showing proportion and return
          </div>
          <b-row class="match-height">
            <b-col cols="7">
              <app-echart-bar
                :option-data="breakdown.productTypes.holdingOptions"
                :height="chartHeights.productTypes + 'px'"
              />
            </b-col>
            <b-col cols="5">
              <app-echart-bar
                :option-data="breakdown.productTypes.returnOptions"
                :height="chartHeights.productTypes + 'px'"
              />
            </b-col>
          </b-row>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BRow, BCol, BCard, BCardTitle, BTable, BButton,
} from 'bootstrap-vue'

import FutureMaturitiesChart from '@/views/products/portfolio/charts/FutureMaturitiesChart.vue'
import MaturedProductsChart from '@/views/products/portfolio/charts/MaturedProductsChart.vue'
import PortfolioEvolutionChart from '@/views/products/portfolio/charts/PortfolioEvolutionChart.vue'
import PayoffScatterChart from '@/views/products/portfolio/charts/PayoffScatterChart.vue'
import LevelsScatterChart from '@/views/products/portfolio/charts/LevelsScatterChart.vue'
import AppEchartBar from '@core/components/charts/echart/AppEchartBar.vue'
import { $themeColors } from '@themeConfig'

const PAYOFF_SCATTER_DATA = {
  options: {
    xmin: '2009-01-01',
    xmax: '2026-06-01',
    ymin: 0,
    ymax: 220,
  },
  data: [
    {
      name: 'Matured',
      data: [
        ['2010-03-09', 110], ['2010-07-26', 115], ['2011-01-04', 109],
        ['2011-10-19', 115], ['2012-05-07', 113], ['2012-09-17', 130],
        ['2013-02-19', 130], ['2013-08-05', 150], ['2013-09-16', 180],
        ['2014-01-20', 200], ['2014-05-29', 191], ['2014-09-29', 150],
        ['2015-03-27', 162], ['2015-05-14', 164], ['2015-09-11', 175],
        ['2016-05-03', 145], ['2016-09-20', 146], ['2017-04-28', 181],
        ['2017-08-24', 150], ['2017-12-01', 180], ['2018-01-19', 180],
        ['2018-06-15', 167], ['2019-04-17', 141], ['2019-09-23', 140],
        ['2020-11-24', 166], ['2021-04-06', 157], ['2021-10-11', 136],
        ['2022-04-20', 160], ['2022-05-31', 170], ['2023-01-30', 154],
        ['2023-03-31', 161], ['2023-05-05', 150], ['2024-05-10', 143],
        ['2024-07-15', 164], ['2024-08-30', 160], ['2024-10-18', 175],
        ['2025-02-28', 157], ['2025-04-17', 207], ['2025-06-12', 176],
        ['2012-03-14', 100], ['2013-02-08', 100], ['2016-07-28', 100],
        ['2019-03-28', 100], ['2020-03-09', 100], ['2021-03-01', 100],
        ['2022-12-28', 100], ['2019-11-11', 0], ['2020-02-14', 0],
        ['2021-04-27', 0], ['2020-05-05', 85],
      ],
    },
    {
      name: 'Live',
      data: [
        ['2023-06-30', 107], ['2023-09-25', 109], ['2023-11-16', 105],
        ['2024-01-29', 104], ['2024-03-25', 112], ['2024-04-11', 110],
        ['2024-05-31', 108], ['2024-06-17', 110], ['2024-07-08', 107],
        ['2024-08-19', 118], ['2024-09-16', 107], ['2024-09-30', 111],
        ['2024-10-14', 115], ['2024-11-19', 116], ['2024-12-16', 109],
        ['2025-01-13', 107], ['2025-01-27', 113], ['2025-02-24', 114],
        ['2025-03-03', 114], ['2025-03-24', 114], ['2025-03-31', 112],
        ['2025-04-28', 115], ['2025-05-06', 115], ['2025-05-30', 115],
        ['2025-06-30', 115], ['2025-07-28', 115], ['2025-08-11', 107],
      ],
    },
  ],
  products: [
    {
      name: 'Matured',
      datalabel: Array(50).fill(''),
      data: Array(50).fill(null),
    },
    {
      name: 'Live',
      datalabel: Array(27).fill(''),
      data: Array(27).fill(null),
    },
  ],
}

const LEVELS_SCATTER_DATA = {
  state_coverscatter: {
    options: {
      xmin: '2025-01-01',
      xmax: '2031-12-01',
    },
    data: [
      {
        name: 'Barrier',
        data: [
          ['2025-06-15', 72], ['2025-09-22', 68], ['2025-11-03', 75],
          ['2026-02-10', 71], ['2026-04-28', 74], ['2026-07-14', 69],
          ['2026-09-30', 73], ['2026-12-07', 70], ['2027-02-21', 76],
          ['2027-05-12', 67], ['2027-08-04', 72], ['2027-10-20', 74],
          ['2028-01-15', 68], ['2028-04-08', 71], ['2028-07-22', 75],
          ['2028-10-14', 69], ['2029-01-28', 73], ['2029-05-06', 70],
          ['2029-08-19', 74], ['2029-11-11', 68], ['2030-02-03', 72],
          ['2030-06-17', 75], ['2030-09-08', 69], ['2030-12-01', 71],
          ['2031-03-22', 74], ['2031-07-14', 70],
        ],
        datalabel: Array(26).fill(''),
        products: Array(26).fill(null),
      },
      {
        name: 'Strike',
        data: [
          ['2025-06-15', 98], ['2025-09-22', 101], ['2025-11-03', 97],
          ['2026-02-10', 99], ['2026-04-28', 102], ['2026-07-14', 96],
          ['2026-09-30', 100], ['2026-12-07', 98], ['2027-02-21', 103],
          ['2027-05-12', 95], ['2027-08-04', 99], ['2027-10-20', 101],
          ['2028-01-15', 97], ['2028-04-08', 100], ['2028-07-22', 102],
          ['2028-10-14', 96], ['2029-01-28', 100], ['2029-05-06', 98],
          ['2029-08-19', 103], ['2029-11-11', 95], ['2030-02-03', 99],
          ['2030-06-17', 102], ['2030-09-08', 97], ['2030-12-01', 100],
          ['2031-03-22', 103], ['2031-07-14', 97],
        ],
        datalabel: Array(26).fill(''),
        products: Array(26).fill(null),
      },
    ],
  },
}

const EVOLUTION_DATA = {
  SHOWDATA: {
    INDEXUNDERLYINGVOLRATIO: '51.9%',
    DATA: [
      {
        name: 'Benchmarks: Investor Benchmark',
        data: [
          { x: '2023-02-10', y: 99.88 }, { x: '2023-04-07', y: 101.35 }, { x: '2023-06-02', y: 100.03 },
          { x: '2023-08-04', y: 99.24 }, { x: '2023-10-06', y: 97.78 }, { x: '2023-12-01', y: 100.05 },
          { x: '2024-02-02', y: 101.73 }, { x: '2024-04-05', y: 103.28 }, { x: '2024-06-07', y: 105.83 },
          { x: '2024-08-02', y: 107.92 }, { x: '2024-10-04', y: 108.96 }, { x: '2024-12-06', y: 109.16 },
          { x: '2025-02-07', y: 110.98 }, { x: '2025-04-04', y: 109.98 }, { x: '2025-06-06', y: 113.81 },
          { x: '2025-08-01', y: 116.46 }, { x: '2025-10-03', y: 120.17 }, { x: '2025-12-05', y: 121.86 },
          { x: '2026-02-06', y: 126.16 },
        ],
      },
      {
        name: 'Benchmarks: Structured Product Index',
        data: [
          { x: '2023-02-10', y: 100.00 }, { x: '2023-04-07', y: 99.67 }, { x: '2023-06-02', y: 100.00 },
          { x: '2023-08-04', y: 101.08 }, { x: '2023-10-06', y: 101.48 }, { x: '2023-12-01', y: 104.16 },
          { x: '2024-02-02', y: 106.43 }, { x: '2024-04-05', y: 109.38 }, { x: '2024-06-07', y: 111.91 },
          { x: '2024-08-02', y: 113.02 }, { x: '2024-10-04', y: 114.95 }, { x: '2024-12-06', y: 116.93 },
          { x: '2025-02-07', y: 119.42 }, { x: '2025-04-04', y: 115.92 }, { x: '2025-06-06', y: 123.61 },
          { x: '2025-08-01', y: 125.34 }, { x: '2025-10-03', y: 128.39 }, { x: '2025-12-05', y: 130.22 },
          { x: '2026-02-06', y: 132.24 },
        ],
      },
    ],
  },
}

const PORTFOLIO_DATA = {
  futurematurities: {
    series: [
      {
        name: 'Possible',
        data: [379, 420, 350, 349, 149, 135, 60, 46, 28, 18, 20, 25],
      },
      {
        name: 'Final',
        data: [44, 37, 55, 71, 99, 91, 158, 171, 208, 227, 230, 588],
      },
    ],
    categories: [
      '2026 H1', '2026 H2', '2027 H1', '2027 H2',
      '2028 H1', '2028 H2', '2029 H1', '2029 H2',
      '2030 H1', '2030 H2', '2031 H1', '2031 H2 +',
    ],
  },
  returns: [
    {
      name: 'All',
      label: 'Percentage share',
      series: [
        {
          name: 'Live',
          data: [1.19, 0.21, 0.21, 0.34, 0.46, 0.95, 1.58, 8.19, 6.78, 2.89, 2.06, 0.55, 0.13, 0.08],
        },
        {
          name: 'Matured',
          data: [0.22, 0.03, 0.04, 0.08, 0.14, 3.74, 7.55, 31.06, 23.61, 5.88, 1.5, 0.34, 0.14, 0.07],
        },
      ],
      xaxis: {
        categories: [
          '<-10%', '<-7.5%', '<-5%', '<-2.5%', '<0%',
          '<2.5%', '<5%', '<7.5%', '<10%', '<12.5%',
          '<15%', '<17.5%', '<20%', '>=20%',
        ],
      },
    },
    {
      name: 'Live',
      label: 'Percentage share',
      data: [
        { x: '<-10%', y: 4.63 }, { x: '<-7.5%', y: 0.81 }, { x: '<-5%', y: 0.81 },
        { x: '<-2.5%', y: 1.32 }, { x: '<0%', y: 1.78 }, { x: '<2.5%', y: 3.72 },
        { x: '<5%', y: 6.16 }, { x: '<7.5%', y: 31.98 }, { x: '<10%', y: 26.48 },
        { x: '<12.5%', y: 11.3 }, { x: '<15%', y: 8.04 }, { x: '<17.5%', y: 2.14 },
        { x: '<20%', y: 0.51 }, { x: '>=20%', y: 0.31 },
      ],
    },
    {
      name: 'Matured',
      label: 'Percentage share',
      data: [
        { x: '<-10%', y: 0.3 }, { x: '<-7.5%', y: 0.04 }, { x: '<-5%', y: 0.05 },
        { x: '<-2.5%', y: 0.11 }, { x: '<0%', y: 0.19 }, { x: '<2.5%', y: 5.03 },
        { x: '<5%', y: 10.15 }, { x: '<7.5%', y: 41.75 }, { x: '<10%', y: 31.74 },
        { x: '<12.5%', y: 7.9 }, { x: '<15%', y: 2.02 }, { x: '<17.5%', y: 0.46 },
        { x: '<20%', y: 0.19 }, { x: '>=20%', y: 0.09 },
      ],
    },
  ],
}

export default {
  name: 'PortfolioReportStatic',
  components: {
    BRow,
    BCol,
    BCard,
    BCardTitle,
    BTable,
    BButton,
    FutureMaturitiesChart,
    MaturedProductsChart,
    PortfolioEvolutionChart,
    PayoffScatterChart,
    LevelsScatterChart,
    AppEchartBar,
  },
  data() {
    const makeOptions = (categories, values, color, hideYAxis = false) => ({
      legend: { show: false },
      grid: {
        left: hideYAxis ? 30 : 160,
        right: 30,
        bottom: 50,
        top: 20,
        containLabel: false,
      },
      xAxis: {
        type: 'value',
        name: hideYAxis ? 'Return' : 'Proportion',
        nameLocation: 'middle',
        nameGap: 30,
        nameTextStyle: { fontSize: 11, fontWeight: 500 },
        axisLabel: { fontSize: 10 },
        splitLine: { show: false },
      },
      yAxis: {
        type: 'category',
        data: categories,
        axisLabel: {
          show: !hideYAxis,
          fontSize: 10,
          margin: 20,
          formatter: v => (v.length > 20 ? `${v.substring(0, 20)}...` : v),
        },
        axisTick: { show: !hideYAxis },
        axisLine: { show: !hideYAxis },
        splitLine: { show: true },
      },
      series: [{
        type: 'bar',
        data: values,
        barWidth: '70%',
        itemStyle: { color },
        label: {
          show: true,
          position: 'inside',
          formatter: p => (typeof p.value === 'number' ? parseFloat(p.value.toFixed(2)) : p.value),
          fontSize: 10,
          color: '#ffffff',
        },
      }],
    })

    const uCats = ['FTSE 100', 'Euro Stoxx 50', 'S&P 500', 'FTSE MIB', 'DAX', 'Nasdaq 100', 'Nikkei 225', 'CAC 40', 'Hang Seng', 'SMI']
    const iCats = ['Barclays', 'Société Générale', 'Goldman Sachs', 'Morgan Stanley', 'BNP Paribas', 'HSBC', 'Deutsche Bank', 'Lloyds']
    const pCats = ['Autocall', 'Reverse Conv.', 'Capital Protected', 'Barrier Note', 'Kick-Out']

    return {
      portfolioData: PORTFOLIO_DATA,
      evolutionData: EVOLUTION_DATA,
      payoffScatterData: PAYOFF_SCATTER_DATA,
      levelsScatterData: LEVELS_SCATTER_DATA,
      chartHeights: {
        underlyings: uCats.length * 26,
        issuers: iCats.length * 26,
        productTypes: pCats.length * 22,
      },
      breakdown: {
        underlyings: {
          holdingOptions: makeOptions(uCats, [28.4, 22.1, 18.7, 9.3, 7.8, 5.2, 4.1, 2.8, 1.2, 0.4], $themeColors.primary),
          returnOptions: makeOptions(uCats, [7.9, 8.4, 6.2, 9.1, 7.5, 5.8, 6.3, 7.1, 4.2, 3.8], $themeColors.warning, true),
        },
        issuers: {
          holdingOptions: makeOptions(iCats, [24.3, 19.8, 17.2, 14.1, 11.6, 7.4, 3.8, 1.8], $themeColors.primary),
          returnOptions: makeOptions(iCats, [7.4, 8.1, 6.9, 7.8, 8.3, 6.5, 7.2, 5.9], $themeColors.warning, true),
        },
        productTypes: {
          holdingOptions: makeOptions(pCats, [52.3, 21.4, 12.8, 8.7, 4.8], $themeColors.primary),
          returnOptions: makeOptions(pCats, [7.6, 4.2, 3.1, 8.9, 9.4], $themeColors.warning, true),
        },
      },
      performanceFields: [
        { label: '', key: 'number' },
        { label: 'All', key: 'all' },
        { label: 'Live', key: 'live' },
        { label: 'Matured', key: 'matured' },
      ],
      performanceItems: [
        {
          number: 'Number',
          all: 8037,
          live: 2132,
          matured: 5905,
        },
        {
          number: 'Avg. Return (Ann.)',
          all: '—',
          live: '—',
          matured: '7.38%',
        },
        {
          number: 'Avg. Duration',
          all: '—',
          live: '2.4y',
          matured: '3.1y',
        },
        {
          number: 'Kicked Out Early',
          all: '—',
          live: '—',
          matured: '71%',
        },
        {
          number: 'Capital Loss',
          all: '—',
          live: '—',
          matured: '4.2%',
        },
        {
          number: 'Avg. Coupon (Ann.)',
          all: '—',
          live: '6.12%',
          matured: '6.84%',
        },
        {
          number: 'Barrier Breach',
          all: '—',
          live: '3.1%',
          matured: '4.2%',
        },
        {
          number: 'Positive Return',
          all: '—',
          live: '—',
          matured: '91.8%',
        },
        {
          number: 'Avg. Strike (vs Index)',
          all: '—',
          live: '97.4%',
          matured: '—',
        },
      ],
    }
  },
}
</script>

<style scoped>
.performance-card {
  justify-content: flex-start;
}

.chart-placeholder {
  background: rgba(130, 134, 139, 0.08);
  border-radius: 6px;
  height: 260px;
}

.chart-placeholder--sm {
  height: 200px;
}
</style>
